import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { API } from '../../utils/config';
import {
  Brain, Plus, Trash2, Send, Globe, MapPin, Loader2, Search,
  FileText, ToggleLeft, ToggleRight, Check, X, Sparkles, BookOpen,
  CalendarClock, Save
} from 'lucide-react';

const AICommandCenter = ({ pages, token }) => {
  const [guidelines, setGuidelines] = useState([]);
  const [newGuideline, setNewGuideline] = useState('');
  const [guidelineScope, setGuidelineScope] = useState('global');
  const [guidelinePageIds, setGuidelinePageIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const [topic, setTopic] = useState('');
  const [topicPageIds, setTopicPageIds] = useState([]);
  const [topicDate, setTopicDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  });
  const [generating, setGenerating] = useState(false);
  const [topicResults, setTopicResults] = useState(null);

  // Frequenza generazione automatica (globale per tutte le pagine)
  const [genFreq, setGenFreq] = useState(7);
  const [genNextDate, setGenNextDate] = useState(null);
  const [savingFreq, setSavingFreq] = useState(false);

  const fetchGuidelines = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/social/ai-guidelines`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setGuidelines(await res.json());
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { fetchGuidelines(); }, [fetchGuidelines]);

  const fetchGenSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/social/generation-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setGenFreq(d.frequency_days || 7);
        setGenNextDate(d.next_auto_generation_date || null);
      }
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { fetchGenSettings(); }, [fetchGenSettings]);

  const saveGenFreq = async () => {
    const n = parseInt(genFreq, 10);
    if (!n || n < 1 || n > 60) { toast.error('Inserisci un numero di giorni tra 1 e 60'); return; }
    setSavingFreq(true);
    try {
      const res = await fetch(`${API}/api/social/generation-settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ frequency_days: n })
      });
      if (res.ok) {
        const d = await res.json();
        setGenNextDate(d.next_auto_generation_date || null);
        toast.success(d.message || `Generazione automatica ogni ${n} giorni`);
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Errore nel salvataggio');
      }
    } catch { toast.error('Errore di rete'); }
    setSavingFreq(false);
  };

  const addGuideline = async () => {
    if (!newGuideline.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/social/ai-guidelines`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: newGuideline.trim(),
          scope: guidelineScope,
          page_ids: guidelineScope === 'pages' ? guidelinePageIds : []
        })
      });
      if (res.ok) {
        setNewGuideline('');
        setGuidelinePageIds([]);
        fetchGuidelines();
        toast.success('Linea guida aggiunta');
      }
    } catch { toast.error('Errore'); }
    setLoading(false);
  };

  const toggleGuideline = async (id, isActive) => {
    await fetch(`${API}/api/social/ai-guidelines/${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !isActive })
    });
    fetchGuidelines();
  };

  const deleteGuideline = async (id) => {
    await fetch(`${API}/api/social/ai-guidelines/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    fetchGuidelines();
    toast.success('Eliminata');
  };

  const toggleTopicPage = (pageId) => {
    setTopicPageIds(prev =>
      prev.includes(pageId) ? prev.filter(p => p !== pageId) : [...prev, pageId]
    );
  };

  const selectAllTopicPages = () => {
    if (topicPageIds.length === pages.length) setTopicPageIds([]);
    else setTopicPageIds(pages.map(p => p.id));
  };

  const toggleGuidelinePage = (pageId) => {
    setGuidelinePageIds(prev =>
      prev.includes(pageId) ? prev.filter(p => p !== pageId) : [...prev, pageId]
    );
  };

  const generateTopicPosts = async () => {
    if (!topic.trim()) { toast.error('Inserisci un argomento'); return; }
    if (topicPageIds.length === 0) { toast.error('Seleziona almeno una pagina'); return; }
    setGenerating(true);
    setTopicResults(null);
    try {
      const res = await fetch(`${API}/api/social/generate-topic-post`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: topic.trim(),
          page_ids: topicPageIds,
          target_date: topicDate
        })
      });
      if (res.ok) {
        const data = await res.json();
        setTopicResults(data);
        toast.success(`${data.pages_processed} post generati`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Errore');
      }
    } catch { toast.error('Errore di rete'); }
    setGenerating(false);
  };

  return (
    <div className="space-y-6" data-testid="ai-command-center">
      {/* SECTION 0: AUTOMAZIONE GENERAZIONE */}
      <Card className="border-slate-200" data-testid="generation-frequency-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-sky-600" />
            Automazione generazione post
          </CardTitle>
          <p className="text-xs text-slate-500">
            Imposta ogni quanti giorni l'AI genera automaticamente i post per tutte le pagine attive. Così crei meno contenuti e non ne sprechi. La generazione manuale resta sempre disponibile.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-700">Genera automaticamente ogni</span>
            <Input
              type="number"
              min={1}
              max={60}
              value={genFreq}
              onChange={e => setGenFreq(e.target.value)}
              className="w-20 text-center"
              data-testid="generation-frequency-input"
            />
            <span className="text-sm text-slate-700">giorni</span>
            <Button size="sm" onClick={saveGenFreq} disabled={savingFreq} className="ml-auto" data-testid="save-generation-frequency-btn">
              {savingFreq ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
              Salva
            </Button>
          </div>
          {genNextDate ? (
            <p className="text-xs text-slate-500 mt-2" data-testid="generation-next-date">
              Prossima generazione automatica prevista: <span className="font-medium text-slate-700">{genNextDate}</span>
            </p>
          ) : (
            <p className="text-xs text-slate-500 mt-2">La prossima generazione automatica avverrà al prossimo ciclo previsto.</p>
          )}
        </CardContent>
      </Card>

      {/* SECTION 1: AI GUIDELINES */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-indigo-600" />
            Linee Guida AI
            <Badge variant="outline" className="ml-auto text-xs">{guidelines.filter(g => g.is_active).length} attive</Badge>
          </CardTitle>
          <p className="text-xs text-slate-500">Istruzioni permanenti che l'AI segue per ogni generazione. Puoi applicarle a tutte le pagine o a pagine specifiche.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Active guidelines list */}
          {guidelines.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {guidelines.map(g => (
                <div key={g.id} className={`flex items-start gap-2 p-2.5 rounded-lg border ${g.is_active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`} data-testid={`guideline-${g.id}`}>
                  <button onClick={() => toggleGuideline(g.id, g.is_active)} className="mt-0.5 shrink-0" title={g.is_active ? 'Disattiva' : 'Attiva'}>
                    {g.is_active ? <ToggleRight className="h-4 w-4 text-emerald-600" /> : <ToggleLeft className="h-4 w-4 text-slate-400" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700">{g.text}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Badge variant="outline" className="text-[10px]">
                        {g.scope === 'global' ? 'Tutte le pagine' : `${(g.page_ids || []).length} pagine`}
                      </Badge>
                    </div>
                  </div>
                  <button onClick={() => deleteGuideline(g.id)} className="text-slate-400 hover:text-rose-500 shrink-0">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add new guideline */}
          <div className="border-t pt-3 space-y-2">
            <textarea
              value={newGuideline}
              onChange={e => setNewGuideline(e.target.value)}
              placeholder="Scrivi una nuova linea guida... (es. 'Non fare più di 1 post sulla cucina a settimana per il Lazio')"
              className="w-full text-sm border rounded-lg p-2.5 resize-none h-20 focus:ring-1 focus:ring-indigo-300 focus:border-indigo-300"
              data-testid="new-guideline-input"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={guidelineScope}
                onChange={e => setGuidelineScope(e.target.value)}
                className="text-xs border rounded px-2 py-1.5"
                data-testid="guideline-scope-select"
              >
                <option value="global">Tutte le pagine</option>
                <option value="pages">Pagine specifiche</option>
              </select>
              {guidelineScope === 'pages' && (
                <div className="flex gap-1 flex-wrap">
                  {pages.map(p => (
                    <button
                      key={p.id}
                      onClick={() => toggleGuidelinePage(p.id)}
                      className={`text-[10px] px-1.5 py-0.5 rounded border ${guidelinePageIds.includes(p.id) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200 text-slate-500'}`}
                    >
                      {(p.region || p.name || '').substring(0, 15)}
                    </button>
                  ))}
                </div>
              )}
              <Button size="sm" onClick={addGuideline} disabled={loading || !newGuideline.trim()} className="ml-auto" data-testid="add-guideline-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SECTION 2: TOPIC POST GENERATOR */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-600" />
            Genera Post su Argomento
          </CardTitle>
          <p className="text-xs text-slate-500">Scrivi o incolla un argomento/notizia. L'AI approfondisce, ricerca online e genera post personalizzati per le pagine che scegli.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <textarea
            value={topic}
            onChange={e => setTopic(e.target.value)}
            placeholder="Scrivi l'argomento o incolla una notizia... (es. 'Domani 22 marzo è la Giornata Mondiale dell'Acqua - parlare delle risorse idriche e del turismo sostenibile')"
            className="w-full text-sm border rounded-lg p-2.5 resize-none h-24 focus:ring-1 focus:ring-amber-300 focus:border-amber-300"
            data-testid="topic-input"
          />
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={topicDate}
              onChange={e => setTopicDate(e.target.value)}
              className="w-36 text-xs"
              data-testid="topic-date-input"
            />
            <button
              onClick={selectAllTopicPages}
              className={`text-xs px-2 py-1 rounded border ${topicPageIds.length === pages.length ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-white border-slate-200 text-slate-500'}`}
              data-testid="select-all-pages-btn"
            >
              <Globe className="h-3 w-3 inline mr-1" />
              {topicPageIds.length === pages.length ? 'Deseleziona tutte' : 'Tutte le pagine'}
            </button>
          </div>

          {/* Page selection */}
          <div className="flex gap-1.5 flex-wrap">
            {pages.map(p => (
              <button
                key={p.id}
                onClick={() => toggleTopicPage(p.id)}
                className={`text-xs px-2 py-1 rounded-full border transition-colors ${topicPageIds.includes(p.id) ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                data-testid={`topic-page-${p.id}`}
              >
                <MapPin className="h-3 w-3 inline mr-0.5" />
                {p.region || p.name}
              </button>
            ))}
          </div>

          <Button
            onClick={generateTopicPosts}
            disabled={generating || !topic.trim() || topicPageIds.length === 0}
            className="w-full bg-amber-600 hover:bg-amber-700"
            data-testid="generate-topic-btn"
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Ricerca e generazione in corso...</>
            ) : (
              <><Search className="h-4 w-4 mr-2" /> Ricerca, Approfondisci e Genera Post</>
            )}
          </Button>

          {/* Results */}
          {topicResults && (
            <div className="border-t pt-3 space-y-2" data-testid="topic-results">
              <p className="text-xs font-medium text-slate-600">
                Risultati per: <span className="text-amber-700">{topicResults.topic}</span>
              </p>
              {topicResults.results?.map((r, i) => (
                <div key={i} className={`flex items-center gap-2 p-2 rounded text-xs ${r.status === 'generated' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                  {r.status === 'generated' ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                  <MapPin className="h-3 w-3" /> {r.page} ({r.region})
                  {r.status === 'generated' && <Badge variant="outline" className="ml-auto text-[10px]">In approvazione</Badge>}
                  {r.error && <span className="ml-auto text-rose-500">{r.error}</span>}
                </div>
              ))}
              <p className="text-[10px] text-slate-400">I post generati sono in attesa di approvazione nella tab Post.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AICommandCenter;
